import { useEffect, useMemo } from 'react'
import { create } from 'zustand'
import { useCurrentProjectItems } from '../ProjectManager'
import { isProjectFolder, visitProjectItems } from '../ProjectItem'

type ProjectSelectionStore = {
  selection: `/${string}` | null
  selectionIsNew: boolean
  select: (selection: `/${string}`, isNew: boolean) => void
  deselect: () => void
}

const useProjectSelectionStore = create<ProjectSelectionStore>((set) => ({
  selection: null,
  selectionIsNew: false,
  select: (selection: `/${string}`, isNew: boolean) => set({ selection, selectionIsNew: isNew }),
  deselect: () => set({ selection: null, selectionIsNew: false })
}))

export function useProjectSelection(): ProjectSelectionStore {
  const currentProjectItems = useCurrentProjectItems()
  const { selection, selectionIsNew, select, deselect } = useProjectSelectionStore()

  const defaultSelection = useMemo(() => {
    if (selection) {
      return null
    }

    const readmeItem = currentProjectItems.find(
      (item) => !isProjectFolder(item) && item.path.toLowerCase() === '/readme.md'
    )
    if (readmeItem) {
      return readmeItem.path
    }

    let defaultSelection: string | null = null
    visitProjectItems(currentProjectItems, (item) => {
      if (isProjectFolder(item)) {
        return
      }
      defaultSelection = item.path
      return true
    })
    return defaultSelection
  }, [currentProjectItems, selection])

  // ensure a file is always selected
  useEffect(() => {
    if (!selection && defaultSelection) {
      select(defaultSelection, false)
    }
  }, [selection, defaultSelection, select])

  return { selection, selectionIsNew, select, deselect }
}
